
import {
	computed,
	defineComponent,
	inject,
	onMounted,
	ref,
	watchEffect,
} from 'vue';
import { FormProvider } from '@/components/ui-elements/form/Form';
import Button from '@/components/ui-elements/form/Button.vue';
import Label from '@/components/ui-elements/form/Label.vue';
import CalendarIcon from '@/components/ui-elements/icons/CalendarIcon.vue';
import ChevronLeftIcon from '@/components/ui-elements/icons/ChevronLeftIcon.vue';
import ChevronRightIcon from '@/components/ui-elements/icons/ChevronRightIcon.vue';
import useTimestamp from '@/hooks/useTimestamp';

export default defineComponent({
	components: {
		Button,
		CalendarIcon,
		ChevronLeftIcon,
		ChevronRightIcon,
		Label,
	},
	props: {
		label: {
			required: true,
			type: String,
		},
		rules: {
			default: () => ({}),
			type: Object,
		},
		value: {
			required: true,
			type: String,
		},
	},
	setup(props, { emit }) {
		const {
			addMonth,
			formatDate,
			formatTimeZone,
			getEndDayOfMonth,
			getStartDayOfMonth,
		} = useTimestamp();
		const dateCounter = ref(0);
		const dateHeader = computed(() =>
			formatDate(
				'yyyy-MM',
				addMonth(dateCounter.value, props.value).toISOString()
			)
		);
		const datePicker = ref(null);
		const dayBegins = computed(() =>
			formatDate(
				'EEEEEE',
				getStartDayOfMonth(
					addMonth(dateCounter.value, props.value).toISOString()
				).toISOString()
			)
		);
		const dayEnds = computed(() =>
			formatDate(
				'EEEEEE',
				getEndDayOfMonth(
					addMonth(dateCounter.value, props.value).toISOString()
				).toISOString()
			)
		);
		const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
		const daysList = computed(() => {
			const buffer = Array.from(
				Array(days.indexOf(dayBegins.value)).keys(),
				num => 60 + num
			);
			const daysOfTheMonth = Array.from(
				Array(
					+formatDate(
						'd',
						getEndDayOfMonth(
							addMonth(
								dateCounter.value,
								props.value
							).toISOString()
						).toISOString()
					)
				).keys()
			);

			return buffer.concat(daysOfTheMonth);
		});
		const labelId = ref<string>('');
		const FormContext = inject<any>(FormProvider, undefined);
		const selected = ref(false);
		const today = formatTimeZone('yyyy-MM-dd');

		onMounted(() => {
			if (props.label && !!FormContext) {
				labelId.value = FormContext.setupForm(props.label, props.rules);
				FormContext.validateField(labelId.value, props.value, true);
			}
		});

		const setSelected = () => (selected.value = false);

		watchEffect(() => {
			if (datePicker.value) {
				if (selected.value) {
					(datePicker.value as any).addEventListener(
						'click',
						(e: any) => e.stopPropagation()
					);
					setTimeout(() => {
						document.body.addEventListener('click', setSelected);
					}, 200);
				} else {
					(datePicker.value as any).removeEventListener(
						'click',
						(e: any) => e.stopPropagation()
					);
					document.body.removeEventListener('click', setSelected);
				}
			}
		});

		const error = computed(() => {
			if (FormContext && FormContext.formElements[labelId.value]) {
				return FormContext.formElements[labelId.value].error;
			}

			return null;
		});

		const setDay = (day: number) => (day < 10 ? `0${day}` : day);

		const isSelected = (day: number) => {
			const result = `${dateHeader.value}-${setDay(day)}`;
			return formatTimeZone('yyyy-MM-dd', 'UTC', props.value) === result;
		};

		const isToday = (day: number) => {
			const result = `${formatTimeZone(
				'yyyy-MM',
				'UTC',
				dateHeader.value
			)}-${setDay(day)}`;
			return today === result;
		};

		const updateValue = (day: number) => {
			selected.value = false;
			const inputValue = `${dateHeader.value}-${setDay(day)}`;
			if (FormContext) {
				FormContext.validateField(labelId.value, inputValue);
			}
			emit('update:value', inputValue);
		};

		return {
			dateCounter,
			dateHeader,
			datePicker,
			dayBegins,
			dayEnds,
			days,
			daysList,
			error,
			formatDate,
			formatTimeZone,
			isSelected,
			isToday,
			labelId,
			selected,
			updateValue,
		};
	},
});
